// API bindings: datasets (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { DatasetFindResult, DatasetCreateResponse, AddLocalDatasetBody, NamedReference, Dataset, ModifyLocalDatasetBody, DatasetUsagePolicy, UsagePolicyCreateResponse, DatasetUsagePolicyCreateBody, DatasetLocalLink, SetDatasetLocalLinkBody, DataSourceDataApiDetails } from "./definitions";

export class ApiDatasets {
    /**
     * Method: GET
     * Path: /datasets
     * Finds datasets
     * Requires permission: dataset.read
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static FindDatasets(queryParams: FindDatasetsQueryParameters): RequestParams<DatasetFindResult, FindDatasetsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/datasets` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /datasets
     * Creates a local dataset
     * Requires permission: dataset.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateDataset(body: AddLocalDatasetBody): RequestParams<DatasetCreateResponse, CreateDatasetErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/datasets`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "RULEBOOK_VIOLATION", handler.badRequestRulebookViolation)
                    .add(400, "INVALID_CONTACT_POINT", handler.badRequestInvalidContactPoint)
                    .add(400, "INVALID_LANDING_PAGE", handler.badRequestInvalidLandingPage)
                    .add(400, "INVALID_ACCESS", handler.badRequestInvalidAccess)
                    .add(400, "INVALID_LICENSE", handler.badRequestInvalidLicense)
                    .add(400, "INVALID_PERIODICITY", handler.badRequestInvalidPeriodicity)
                    .add(400, "INVALID_DATES", handler.badRequestInvalidDates)
                    .add(400, "INVALID_SPATIAL", handler.badRequestInvalidSpatial)
                    .add(400, "INVALID_PUBLISHER", handler.badRequestInvalidPublisher)
                    .add(400, "INVALID_NEGOTIATION_AUTOMATION_POLICY", handler.badRequestInvalidNegotiationAutomationPolicy)
                    .add(400, "INVALID_PAYMENT_INTERVAL", handler.badRequestInvalidPaymentInterval)
                    .add(400, "UNSUPPORTED_CURRENCY", handler.badRequestUnsupportedCurrency)
                    .add(400, "PRICE_AMOUNT_TOO_BIG", handler.badRequestPriceAmountTooBig)
                    .add(400, "PRICE_AMOUNT_TOO_SMALL", handler.badRequestPriceAmountTooSmall)
                    .add(400, "UNRECOGNIZED_PAYMENT_METHOD", handler.badRequestUnrecognizedPaymentMethod)
                    .add(400, "EMPTY_PAYMENT_METHODS", handler.badRequestEmptyPaymentMethods)
                    .add(400, "MISSING_USAGE_POLICY", handler.badRequestMissingUsagePolicy)
                    .add(400, "INVALID_USAGE_POLICIES", handler.badRequestInvalidUsagePolicies)
                    .add(400, "INVALID_DATA_SOURCE_RESTRICTIONS", handler.badRequestInvalidDataSourceRestrictions)
                    .add(400, "INVALID_DATA_SOURCE", handler.badRequestInvalidDataSource)
                    .add(400, "INVALID_ISSUED", handler.badRequestInvalidIssued)
                    .add(400, "INVALID_IDENTIFIER", handler.badRequestInvalidIdentifier)
                    .add(400, "INVALID_CREATOR", handler.badRequestInvalidCreator)
                    .add(400, "INVALID_CONFORMS_TO", handler.badRequestInvalidConformsTo)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_TITLE", handler.badRequestInvalidTitle)
                    .add(400, "INVALID_CATALOG", handler.badRequestInvalidCatalog)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /datasets/providers
     * Lists the participants providing remote datasets, to filter the datasets by them
     * Requires permission: dataset.read
     * @returns The request parameters
     */
    public static GetDatasetProviders(): RequestParams<NamedReference[], GetDatasetProvidersErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/datasets/providers`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /datasets/{id}
     * Get dataset
     * Requires permission: dataset.read
     * @param id Dataset ID
     * @returns The request parameters
     */
    public static GetDataset(id: string): RequestParams<Dataset, GetDatasetErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /datasets/{id}
     * Modifies a local dataset
     * Requires permission: dataset.write
     * @param id Dataset ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifyDataset(id: string, body: ModifyLocalDatasetBody): RequestParams<void, ModifyDatasetErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_CONTACT_POINT", handler.badRequestInvalidContactPoint)
                    .add(400, "INVALID_LANDING_PAGE", handler.badRequestInvalidLandingPage)
                    .add(400, "INVALID_ACCESS", handler.badRequestInvalidAccess)
                    .add(400, "INVALID_LICENSE", handler.badRequestInvalidLicense)
                    .add(400, "INVALID_PERIODICITY", handler.badRequestInvalidPeriodicity)
                    .add(400, "INVALID_DATES", handler.badRequestInvalidDates)
                    .add(400, "INVALID_SPATIAL", handler.badRequestInvalidSpatial)
                    .add(400, "INVALID_PUBLISHER", handler.badRequestInvalidPublisher)
                    .add(400, "INVALID_CATALOG", handler.badRequestInvalidCatalog)
                    .add(400, "INVALID_ISSUED", handler.badRequestInvalidIssued)
                    .add(400, "INVALID_IDENTIFIER", handler.badRequestInvalidIdentifier)
                    .add(400, "INVALID_CREATOR", handler.badRequestInvalidCreator)
                    .add(400, "INVALID_CONFORMS_TO", handler.badRequestInvalidConformsTo)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_TITLE", handler.badRequestInvalidTitle)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /datasets/{id}
     * Deletes a dataset
     * Requires permission: dataset.write
     * @param id Dataset ID
     * @returns The request parameters
     */
    public static DeleteDataset(id: string): RequestParams<void, DeleteDatasetErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /datasets/{id}/policies
     * Get dataset usage policies
     * Requires permission: dataset.read
     * @param id Dataset ID
     * @returns The request parameters
     */
    public static GetUsagePolicies(id: string): RequestParams<DatasetUsagePolicy[], GetUsagePoliciesErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}/policies`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /datasets/{id}/policies
     * Adds usage policy to a local dataset
     * Requires permission: dataset.write
     * @param id Dataset ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static AddUsagePolicy(id: string, body: DatasetUsagePolicyCreateBody): RequestParams<UsagePolicyCreateResponse, AddUsagePolicyErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}/policies`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "RULEBOOK_VIOLATION", handler.badRequestRulebookViolation)
                    .add(400, "INVALID_PAYMENT_INTERVAL", handler.badRequestInvalidPaymentInterval)
                    .add(400, "UNSUPPORTED_CURRENCY", handler.badRequestUnsupportedCurrency)
                    .add(400, "PRICE_AMOUNT_TOO_BIG", handler.badRequestPriceAmountTooBig)
                    .add(400, "PRICE_AMOUNT_TOO_SMALL", handler.badRequestPriceAmountTooSmall)
                    .add(400, "UNRECOGNIZED_PAYMENT_METHOD", handler.badRequestUnrecognizedPaymentMethod)
                    .add(400, "EMPTY_PAYMENT_METHODS", handler.badRequestEmptyPaymentMethods)
                    .add(400, "INVALID_EMPTY", handler.badRequestInvalidEmpty)
                    .add(400, "INVALID_PROHIBITIONS", handler.badRequestInvalidProhibitions)
                    .add(400, "INVALID_PERMISSIONS", handler.badRequestInvalidPermissions)
                    .add(400, "INVALID_OBLIGATIONS", handler.badRequestInvalidObligations)
                    .add(400, "TOO_MANY_POLICIES", handler.badRequestTooManyPolicies)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /datasets/{id}/local
     * Get dataset local link
     * Requires permission: dataset.read
     * @param id Dataset ID
     * @returns The request parameters
     */
    public static GetLocalLink(id: string): RequestParams<DatasetLocalLink, GetLocalLinkErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}/local`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /datasets/{id}/local
     * Sets link to a local data source
     * Requires permission: dataset.write
     * @param id Dataset ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static SetLocalLink(id: string, body: SetDatasetLocalLinkBody): RequestParams<void, SetLocalLinkErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}/local`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_NEGOTIATION_AUTOMATION_POLICY", handler.badRequestInvalidNegotiationAutomationPolicy)
                    .add(400, "INVALID_DATA_SOURCE_RESTRICTIONS", handler.badRequestInvalidDataSourceRestrictions)
                    .add(400, "INVALID_DATA_SOURCE", handler.badRequestInvalidDataSource)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /datasets/{id}/local/data
     * Gets dataset data API URL and authentication details
     * Requires permission: dataset.read
     * @param id Dataset ID
     * @returns The request parameters
     */
    public static GetLocalData(id: string): RequestParams<DataSourceDataApiDetails, GetLocalDataErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}/local/data`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /datasets/{id}/policies/{pid}
     * Modifies usage policy of a local dataset
     * Requires permission: dataset.write
     * @param id Dataset ID
     * @param pid Policy ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifyUsagePolicy(id: string, pid: string, body: DatasetUsagePolicyCreateBody): RequestParams<void, ModifyUsagePolicyErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}/policies/${encodeURIComponent(pid)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "POLICY_NOT_FOUND", handler.notFoundPolicyNotFound)
                    .add(404, "DATASET_NOT_LOCAL", handler.notFoundDatasetNotLocal)
                    .add(404, "NOT_FOUND", handler.notFoundNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "RULEBOOK_VIOLATION", handler.badRequestRulebookViolation)
                    .add(400, "INVALID_PAYMENT_INTERVAL", handler.badRequestInvalidPaymentInterval)
                    .add(400, "UNSUPPORTED_CURRENCY", handler.badRequestUnsupportedCurrency)
                    .add(400, "PRICE_AMOUNT_TOO_BIG", handler.badRequestPriceAmountTooBig)
                    .add(400, "PRICE_AMOUNT_TOO_SMALL", handler.badRequestPriceAmountTooSmall)
                    .add(400, "UNRECOGNIZED_PAYMENT_METHOD", handler.badRequestUnrecognizedPaymentMethod)
                    .add(400, "EMPTY_PAYMENT_METHODS", handler.badRequestEmptyPaymentMethods)
                    .add(400, "INVALID_EMPTY", handler.badRequestInvalidEmpty)
                    .add(400, "INVALID_PROHIBITIONS", handler.badRequestInvalidProhibitions)
                    .add(400, "INVALID_PERMISSIONS", handler.badRequestInvalidPermissions)
                    .add(400, "INVALID_OBLIGATIONS", handler.badRequestInvalidObligations)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /datasets/{id}/policies/{pid}
     * Deletes usage policy of a local dataset
     * Requires permission: dataset.write
     * @param id Dataset ID
     * @param pid Policy ID
     * @returns The request parameters
     */
    public static DeleteUsagePolicy(id: string, pid: string): RequestParams<void, DeleteUsagePolicyErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/datasets/${encodeURIComponent(id)}/policies/${encodeURIComponent(pid)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "LAST_USAGE_POLICY", handler.badRequestLastUsagePolicy)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for FindDatasets
 */
export type FindDatasetsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for FindDatasets
 */
export interface FindDatasetsQueryParameters {
    /**
     * Local - eg: true,false
     */
    local?: string;

    /**
     * Text to filter by title
     */
    title?: string;

    /**
     * Text to filter by creator
     */
    creator?: string;

    /**
     * Preferred language for texts
     */
    lang?: string;

    /**
     * Keywords separated by commas. Matches datasets having all of them
     */
    keywords?: string;

    /**
     * Themes separated by commas. Matches datasets having all of them
     */
    themes?: string;

    /**
     * Keywords separated by commas. Matches datasets having any of them
     */
    anyKeywords?: string;

    /**
     * Themes separated by commas. Matches datasets having any of them
     */
    anyThemes?: string;

    /**
     * Issued date range. Separate the 2 dates by a comma
     */
    issued?: string;

    /**
     * Modified date range. Separate the 2 dates by a comma
     */
    modified?: string;

    /**
     * ID of the catalog
     */
    catalog?: string;

    /**
     * ID of the participant providing the datasets (remote datasets only)
     */
    provider?: string;

    /**
     * LIst length limit. Default: 25, Max: 256
     */
    limit?: number;

    /**
     * Continuation token
     */
    continuationToken?: string;
}

/**
 * Error handler for CreateDataset
 */
export type CreateDatasetErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid catalog
     */
    badRequestInvalidCatalog: () => void;

    /**
     * Invalid title
     */
    badRequestInvalidTitle: () => void;

    /**
     * Invalid description (too big for database)
     */
    badRequestInvalidDescription: () => void;

    /**
     * Invalid conformsTo
     */
    badRequestInvalidConformsTo: () => void;

    /**
     * Invalid creator
     */
    badRequestInvalidCreator: () => void;

    /**
     * Invalid identifier
     */
    badRequestInvalidIdentifier: () => void;

    /**
     * Invalid issued
     */
    badRequestInvalidIssued: () => void;

    /**
     * Invalid data source ID
     */
    badRequestInvalidDataSource: () => void;

    /**
     * Invalid data source restrictions (too big for database)
     */
    badRequestInvalidDataSourceRestrictions: () => void;

    /**
     * Invalid usage policies
     */
    badRequestInvalidUsagePolicies: () => void;

    /**
     * The dataset has no usage policy with at least one permission or prohibition
     */
    badRequestMissingUsagePolicy: () => void;

    /**
     * Empty list of payment methods
     */
    badRequestEmptyPaymentMethods: () => void;

    /**
     * At least one payment method is unrecognized
     */
    badRequestUnrecognizedPaymentMethod: () => void;

    /**
     * Invalid price (too small)
     */
    badRequestPriceAmountTooSmall: () => void;

    /**
     * Invalid price (too big)
     */
    badRequestPriceAmountTooBig: () => void;

    /**
     * Unsupported currency
     */
    badRequestUnsupportedCurrency: () => void;

    /**
     * Invalid payment interval
     */
    badRequestInvalidPaymentInterval: () => void;

    /**
     * Invalid negotiation automation policy
     */
    badRequestInvalidNegotiationAutomationPolicy: () => void;

    /**
     * Invalid publisher (Max length: 255)
     */
    badRequestInvalidPublisher: () => void;

    /**
     * Invalid spatial (Max length: 255)
     */
    badRequestInvalidSpatial: () => void;

    /**
     * Invalid temporal (empty value)
     */
    badRequestInvalidDates: () => void;

    /**
     * Invalid accrual periodicity (Max length: 100)
     */
    badRequestInvalidPeriodicity: () => void;

    /**
     * Invalid license (Max length: 255)
     */
    badRequestInvalidLicense: () => void;

    /**
     * Invalid access rights (Max length: 100)
     */
    badRequestInvalidAccess: () => void;

    /**
     * Invalid landing page (Max length: 500)
     */
    badRequestInvalidLandingPage: () => void;

    /**
     * Invalid contact point
     */
    badRequestInvalidContactPoint: () => void;

    /**
     * A usage policy goes against an approved rule of the rulebook. Check it with CheckUsagePolicyAgainstRulebook to know which
     */
    badRequestRulebookViolation: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetDatasetProviders
 */
export type GetDatasetProvidersErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetDataset
 */
export type GetDatasetErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ModifyDataset
 */
export type ModifyDatasetErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid title
     */
    badRequestInvalidTitle: () => void;

    /**
     * Invalid description (too big for database)
     */
    badRequestInvalidDescription: () => void;

    /**
     * Invalid conformsTo
     */
    badRequestInvalidConformsTo: () => void;

    /**
     * Invalid creator
     */
    badRequestInvalidCreator: () => void;

    /**
     * Invalid identifier
     */
    badRequestInvalidIdentifier: () => void;

    /**
     * Invalid issued
     */
    badRequestInvalidIssued: () => void;

    /**
     * Invalid catalog
     */
    badRequestInvalidCatalog: () => void;

    /**
     * Invalid publisher (Max length: 255)
     */
    badRequestInvalidPublisher: () => void;

    /**
     * Invalid spatial (Max length: 255)
     */
    badRequestInvalidSpatial: () => void;

    /**
     * Invalid temporal (empty value)
     */
    badRequestInvalidDates: () => void;

    /**
     * Invalid accrual periodicity (Max length: 100)
     */
    badRequestInvalidPeriodicity: () => void;

    /**
     * Invalid license (Max length: 255)
     */
    badRequestInvalidLicense: () => void;

    /**
     * Invalid access rights (Max length: 100)
     */
    badRequestInvalidAccess: () => void;

    /**
     * Invalid landing page (Max length: 500)
     */
    badRequestInvalidLandingPage: () => void;

    /**
     * Invalid contact point
     */
    badRequestInvalidContactPoint: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteDataset
 */
export type DeleteDatasetErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetUsagePolicies
 */
export type GetUsagePoliciesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for AddUsagePolicy
 */
export type AddUsagePolicyErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The dataset reached the limit of policies it can have
     */
    badRequestTooManyPolicies: () => void;

    /**
     * Invalid obligations
     */
    badRequestInvalidObligations: () => void;

    /**
     * Invalid permissions (too big for db)
     */
    badRequestInvalidPermissions: () => void;

    /**
     * Invalid prohibitions
     */
    badRequestInvalidProhibitions: () => void;

    /**
     * You did not specify any permissions or prohibitions
     */
    badRequestInvalidEmpty: () => void;

    /**
     * Empty list of payment methods
     */
    badRequestEmptyPaymentMethods: () => void;

    /**
     * At least one payment method is unrecognized
     */
    badRequestUnrecognizedPaymentMethod: () => void;

    /**
     * Invalid price (too small)
     */
    badRequestPriceAmountTooSmall: () => void;

    /**
     * Invalid price (too big)
     */
    badRequestPriceAmountTooBig: () => void;

    /**
     * Unsupported currency
     */
    badRequestUnsupportedCurrency: () => void;

    /**
     * Invalid payment interval
     */
    badRequestInvalidPaymentInterval: () => void;

    /**
     * The policy goes against an approved rule of the rulebook. Check it with CheckUsagePolicyAgainstRulebook to know which
     */
    badRequestRulebookViolation: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for GetLocalLink
 */
export type GetLocalLinkErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for SetLocalLink
 */
export type SetLocalLinkErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid data source ID
     */
    badRequestInvalidDataSource: () => void;

    /**
     * Invalid data source restrictions (too big for database)
     */
    badRequestInvalidDataSourceRestrictions: () => void;

    /**
     * Invalid negotiation automation policy
     */
    badRequestInvalidNegotiationAutomationPolicy: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for GetLocalData
 */
export type GetLocalDataErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ModifyUsagePolicy
 */
export type ModifyUsagePolicyErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid obligations
     */
    badRequestInvalidObligations: () => void;

    /**
     * Invalid permissions (too big for db)
     */
    badRequestInvalidPermissions: () => void;

    /**
     * Invalid prohibitions
     */
    badRequestInvalidProhibitions: () => void;

    /**
     * You did not specify any permissions or prohibitions
     */
    badRequestInvalidEmpty: () => void;

    /**
     * Empty list of payment methods
     */
    badRequestEmptyPaymentMethods: () => void;

    /**
     * At least one payment method is unrecognized
     */
    badRequestUnrecognizedPaymentMethod: () => void;

    /**
     * Invalid price (too small)
     */
    badRequestPriceAmountTooSmall: () => void;

    /**
     * Invalid price (too big)
     */
    badRequestPriceAmountTooBig: () => void;

    /**
     * Unsupported currency
     */
    badRequestUnsupportedCurrency: () => void;

    /**
     * Invalid payment interval
     */
    badRequestInvalidPaymentInterval: () => void;

    /**
     * The policy goes against an approved rule of the rulebook. Check it with CheckUsagePolicyAgainstRulebook to know which
     */
    badRequestRulebookViolation: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Dataset not found
     */
    notFoundNotFound: () => void;

    /**
     * Not a local dataset
     */
    notFoundDatasetNotLocal: () => void;

    /**
     * Policy not found
     */
    notFoundPolicyNotFound: () => void;
};

/**
 * Error handler for DeleteUsagePolicy
 */
export type DeleteUsagePolicyErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The policy is the last one of the dataset, which must keep at least one
     */
    badRequestLastUsagePolicy: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

